import { useState } from "preact/hooks";
import { getRdApiKey, setRdApiKey, clearRdApiKey, isHistoryEnabled, setHistoryEnabled, clearHistory } from "./api.ts";
import { PairView } from "./PairView.tsx";
import { appStore } from "./store.ts";
import { useStore } from "./useStore.ts";

interface SettingsViewProps {
  onSaved: () => void;
}

export function SettingsView({ onSaved }: SettingsViewProps) {
  const connected = useStore(appStore, (s) => s.connected);
  const roomStatus = useStore(appStore, (s) => s.roomStatus);
  const controllerName = useStore(appStore, (s) => s.controllerName);

  const [rdKeyInput, setRdKeyInput] = useState(getRdApiKey() || "");
  const [error, setError] = useState("");

  const [editingRoomName, setEditingRoomName] = useState(false);
  const [roomNameInput, setRoomNameInput] = useState("");

  const [editingControllerName, setEditingControllerName] = useState(false);
  const [controllerNameInput, setControllerNameInput] = useState("");

  const [historyEnabled, setHistoryEnabledState] = useState(isHistoryEnabled);

  function handleSave() {
    const key = rdKeyInput.trim();
    if (!key) {
      setError("Enter an API key");
      return;
    }
    setError("");
    setRdApiKey(key);
    onSaved();
  }

  return (
    <div style={{ marginTop: "20px" }}>
      <h3>Pairing</h3>

      <PairView />

      {connected && (
        <>
          <hr style={{ margin: "24px 0", border: "none", borderTop: "1px solid #ddd" }} />

          <h3>Room Name</h3>
          {editingRoomName ? (
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                value={roomNameInput}
                onInput={(e) =>
                  setRoomNameInput((e.target as HTMLInputElement).value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    appStore.getState().updateRoomName(roomNameInput.trim());
                    setEditingRoomName(false);
                  }
                }}
                placeholder="Enter room name"
                style={{
                  flex: 1,
                  padding: "8px",
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={() => {
                  appStore.getState().updateRoomName(roomNameInput.trim());
                  setEditingRoomName(false);
                }}
                style={{ padding: "8px 16px" }}
              >
                Save
              </button>
              <button
                onClick={() => setEditingRoomName(false)}
                style={{ padding: "8px 16px" }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>{roomStatus?.roomName || "(no name)"}</span>
              <button
                onClick={() => {
                  setRoomNameInput(roomStatus?.roomName || "");
                  setEditingRoomName(true);
                }}
                style={{ padding: "4px 12px", fontSize: "13px" }}
              >
                Edit
              </button>
            </div>
          )}

          <hr style={{ margin: "24px 0", border: "none", borderTop: "1px solid #ddd" }} />

          <h3>Controller Name</h3>
          {editingControllerName ? (
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                value={controllerNameInput}
                onInput={(e) =>
                  setControllerNameInput((e.target as HTMLInputElement).value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    appStore.getState().updateControllerName(controllerNameInput.trim());
                    setEditingControllerName(false);
                  }
                }}
                placeholder="Enter your name"
                style={{
                  flex: 1,
                  padding: "8px",
                  boxSizing: "border-box",
                }}
              />
              <button
                onClick={() => {
                  appStore.getState().updateControllerName(controllerNameInput.trim());
                  setEditingControllerName(false);
                }}
                style={{ padding: "8px 16px" }}
              >
                Save
              </button>
              <button
                onClick={() => setEditingControllerName(false)}
                style={{ padding: "8px 16px" }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>{controllerName || "(no name)"}</span>
              <button
                onClick={() => {
                  setControllerNameInput(controllerName);
                  setEditingControllerName(true);
                }}
                style={{ padding: "4px 12px", fontSize: "13px" }}
              >
                Edit
              </button>
            </div>
          )}
        </>
      )}

      <hr style={{ margin: "24px 0", border: "none", borderTop: "1px solid #ddd" }} />

      <h3>Watch History</h3>
      <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={historyEnabled}
          onChange={(e) => {
            const enabled = (e.target as HTMLInputElement).checked;
            setHistoryEnabledState(enabled);
            setHistoryEnabled(enabled);
          }}
        />
        Enable watch history
      </label>
      <button
        onClick={() => {
          clearHistory();
        }}
        style={{ marginTop: "12px", padding: "6px 14px", fontSize: "13px" }}
      >
        Clear History
      </button>

      <hr style={{ margin: "24px 0", border: "none", borderTop: "1px solid #ddd" }} />

      <h3>Real-Debrid API Key</h3>

      {error && <p style={{ color: "red" }}>{error}</p>}
      <input
        type="password"
        value={rdKeyInput}
        onInput={(e) =>
          setRdKeyInput((e.target as HTMLInputElement).value)
        }
        placeholder="Enter your RD API key"
        style={{
          width: "100%",
          padding: "8px",
          marginBottom: "8px",
          boxSizing: "border-box",
          fontFamily: "monospace",
        }}
      />
      <div style={{ display: "flex", gap: "8px" }}>
        <button
          onClick={handleSave}
          style={{ padding: "8px 16px" }}
        >
          Save
        </button>
        <button
          onClick={() => {
            clearRdApiKey();
            setRdKeyInput("");
          }}
          style={{ padding: "8px 16px" }}
        >
          Clear Key
        </button>
      </div>
      <p style={{ fontSize: "12px", color: "#888", marginTop: "12px" }}>
        Get your API key from{" "}
        <a
          href="https://real-debrid.com/apitoken"
          target="_blank"
          rel="noopener"
        >
          real-debrid.com/apitoken
        </a>
      </p>
    </div>
  );
}
